import { OrganizationLevelType } from '@/api/organizations';
import { subOrganizationsQueryOptions } from '@/api/organizationsQueryOptions';
import { fetchDetailPermissionsQueryOptions, fetchReportPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import { fetchRegistrationReport, fetchRegistrationReportOrg } from '@/api/registration';
import { registrationRecordsQueryOptions, registrationSnapshotsQueryOptions, useCreateManualRegistrationSnapshotMutation } from '@/api/registrationQueryOptions';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import useBasicGridColumns from '@/components/grid/BasicGridColumns';
import useBasicValueOptions from '@/components/grid/BasicValueOptions';
import useGridPageSize from '@/components/hooks/useGridPageSize';
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import useResponsive from '@/components/hooks/useResponsive';
import LoadProgress from '@/components/LoadProgress';
import { ScoregQuickFilter } from '@/components/mui-x/datagrid/filter/ScoregQuickFilter';
import GridToolbarPaginationButton from '@/components/mui-x/toolbar/GridToolbarPaginationButton';
import { GridToolbarReportContainer } from '@/components/mui-x/toolbar/GridToolbarReportContainer';
import { getOrganization } from '@/store/auth/authSlice';
import MissingFullIcon from '@/ui/icons/MissingFullIcon';
import NotRegisteredIcon from '@/ui/icons/NotRegisteredIcon';
import RegisteredAddIcon from '@/ui/icons/RegisteredAddIcon';
import RegisteredElseIcon from '@/ui/icons/RegisteredElseIcon';
import RegisteredFullIcon from '@/ui/icons/RegisteredFullIcon';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Button, ButtonProps, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, InputLabel, ListItemIcon, ListItemText, MenuItem, Select, SelectChangeEvent, TextField, Tooltip } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridExportMenuItemProps, GridLogicOperator, GridPagination, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid-premium/locales';
import { Add, Equal, Remove } from '@nine-thirty-five/material-symbols-react/sharp';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Buffer } from 'buffer';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

export const Route = createFileRoute('/_auth/registration')({
    component: RegistrationOverviewComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function RegistrationOverviewComponent() {

    const sessionOrganization = useSelector(getOrganization);

    const registrationSnapshotQuery = useSuspenseQuery(registrationSnapshotsQueryOptions())
    const registrationSnapshots = registrationSnapshotQuery.data;

    const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('organization', sessionOrganization.uuid, ['RegistrationSnapshot']))
    const permissions = permissionsQuery.data

    const reportPermissionQuery = useSuspenseQuery(fetchReportPermissionsQueryOptions(['scoreg_abrechnung']))
    const reportPermissions = reportPermissionQuery.data

    const createSnapshot = permissions && permissions.filter(p => p.detail === 'RegistrationSnapshot' && p.permissions.some(a => a === 'create')).length > 0

    const scoregAbrechnung = reportPermissions && reportPermissions.filter(p => p.detail === 'scoreg_abrechnung' && p.permissions.some(a => a === 'read')).length > 0

    const [selectedSnapshotUuid, setSelectedSnapshotUuid] = useState<string>((registrationSnapshots && registrationSnapshots[0].uuid) || '');
    const [selectedCompareUuid, setSelectedCompareUuid] = useState<string>('');

    const handleSnapshotChange = (event: SelectChangeEvent) => {
        setSelectedSnapshotUuid(event.target.value as string);
    };

    const handleCompareChange = (event: SelectChangeEvent) => {
        setSelectedCompareUuid(event.target.value as string);
    };

    const registrationQuery = useSuspenseQuery(registrationRecordsQueryOptions(selectedSnapshotUuid, sessionOrganization.uuid, selectedCompareUuid))
    const registrations = registrationQuery.data;

    const organizationsQuery = useSuspenseQuery(subOrganizationsQueryOptions())
    const organizations = organizationsQuery.data;

    const createManualSnapshotMutation = useCreateManualRegistrationSnapshotMutation(() => {
        enqueueSnackbar(t('action.manualSnapshotCreationSuccess'), { variant: 'success' });
    },
        (error) => {
            enqueueSnackbar(t('action.manualSnapshotCreationFailed') + error.message, { variant: 'error' })
        });

    // const navigate = useNavigate();

    const sections = [...new Set(registrations.map((reg) => reg.sectionMedusaAbbreviation || '').filter((sec) => sec !== ''))].sort()
    const troops = [...new Set(registrations.filter((reg) => (reg.troopName || '').length > 0).map((reg) => reg.troopName))].sort()
    const divisions = [...new Set(registrations.filter((reg) => (reg.divisionName || '').length > 0).map((reg) => reg.divisionName))].sort()

    const current = registrations.filter((reg) => reg.compareType != 'DELETED')

    const total = current ? current.length : 0;

    const memberFull = current ? current.filter((e) => e.scoutstate == 'MEMBER_FULL').length : 0;
    const memberAdd = current ? current.filter((e) => e.scoutstate == 'MEMBER_ADD').length : 0;

    const totalComp = registrations ? registrations.filter((e) => e.compareType != 'NEW').length : 0;

    const memberFullComp = registrations ? registrations.filter((e) => e.compareType != 'NEW' && e.scoutstate == 'MEMBER_FULL').length : 0;
    const memberAddComp = registrations ? registrations.filter((e) => e.compareType != 'NEW' && e.scoutstate == 'MEMBER_ADD').length : 0;

    const totalDiff = total - totalComp;

    const memberFullDiff = memberFull - memberFullComp;
    const memberAddDiff = memberAdd - memberAddComp;

    const snapshotVersion = registrationSnapshots.find((snapshot) => snapshot.uuid == selectedSnapshotUuid)?.version || 0;
    const compareVersion = registrationSnapshots.find((snapshot) => snapshot.uuid == selectedCompareUuid)?.version || 0;

    const { xsmall, small, medium, large, xlarge } = useResponsive();

    const basicValueOptions = useBasicValueOptions();
    const basicGridColumns = useBasicGridColumns();

    const columns: GridColDef[] = [];
    columns.push({
        field: 'scoutId',
        headerName: 'Scout-ID',
        valueGetter: (_value, row) => `${row.scoutId}`,
        renderCell: (params) => {
            return (
                <Link to='/member/$memberUuid/main' params={{ memberUuid: params.row.memberUuid }}>
                    {params.row.scoutId}
                </Link>
            )
        },

    })
    if (xsmall || small || medium) {
        columns.push({
            field: 'member',
            headerName: 'Member',
            valueGetter: (_value, row) =>
                `${row.scoutId} - ${row.firstname} ${row.lastname}`,
            renderCell: (params) => {
                return (
                    <Link
                        to="/member/$memberUuid/main"
                        params={{
                            memberUuid: params.row.memberUuid,
                        }}
                        preload="intent"
                    >
                        {params.row.scoutId} - {params.row.firstname} {params.row.lastname}
                    </Link>
                )
            },
        },
        )
    } else {
        columns.push(
            {
                field: 'firstname',
                headerName: 'Vorname',
                valueGetter: (_value, row) => `${row.firstname}`,
                renderCell: (params) => {
                    return (
                        <Link to='/member/$memberUuid/main' params={{ memberUuid: params.row.memberUuid }}>
                            {params.row.firstname}
                        </Link>
                    )
                },
            },
            {
                field: 'lastname',
                headerName: 'Nachname',
                valueGetter: (_value, row) => `${row.lastname}`,
                renderCell: (params) => {
                    return (
                        <Link to='/member/$memberUuid/main' params={{ memberUuid: params.row.memberUuid }}>
                            {params.row.lastname}
                        </Link>
                    )
                },
            },
            {
                field: 'birthdate',
                headerName: 'Geburtsdatum',
                type: 'date',
                valueGetter: (value) => value && new Date(value),
                minWidth: 120,

            }
        )
    }
    columns.push(
        {
            field: 'sectionMedusaAbbreviation',
            headerName: t('property.registrationRecord.section'),
            type: 'singleSelect',
            valueOptions: sections,
        },
    )

    columns.push(
        {
            field: 'jobs',
            headerName: t('property.registrationRecord.jobs'),
            valueGetter: (_value, row) => [row.troopJobs, row.disivionJobs, row.federationJobs].filter(Boolean).join(", "),

        },
    )

    if (sessionOrganization.organizationLevel == 'DIVISION' || sessionOrganization.organizationLevel == 'FEDERATION') {
        columns.push(
            {
                field: 'troopName',
                headerName: t('property.registrationRecord.troopName'),
                type: 'singleSelect',
                valueOptions: troops,

            },
        )
    }
    if (sessionOrganization.organizationLevel == 'FEDERATION') {
        columns.push(
            {
                field: 'divisionName',
                headerName: t('property.registrationRecord.divisionName'),
                type: 'singleSelect',
                valueOptions: divisions,

            },
        )
    }
    columns.push(basicGridColumns.scoutstateColumn)
    columns.push(
        {
            field: 'registrationStatus',
            headerName: t('property.registrationRecord.registrationStatus'),
            type: 'singleSelect',
            valueOptions: basicValueOptions.registrationStatusOptions,
            valueGetter: (_value, row) => `${row.registrationStatus}`,
            renderCell: (params) => {
                const registrationStatus = params.row.registrationStatus;

                if ('REGISTERED_FULL' == registrationStatus) {
                    return (
                        <Tooltip title={t('enum.registrationStatus.REGISTERED_FULL')}>
                            <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                                <RegisteredFullIcon />
                            </Box>
                        </Tooltip>
                    )
                } else if ('REGISTERED_ADD' == registrationStatus) {
                    return (
                        <Tooltip title={t('enum.registrationStatus.REGISTERED_ADD')}>
                            <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                                <RegisteredAddIcon />
                            </Box>
                        </Tooltip>
                    )
                } else if ('REGISTERED_ELSE' == registrationStatus) {
                    return (
                        <Tooltip title={t('enum.registrationStatus.REGISTERED_ELSE')}>
                            <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                                <RegisteredElseIcon />
                            </Box>
                        </Tooltip>
                    )
                } else if ('NOT_REGISTERED' == registrationStatus) {
                    return (
                        <Tooltip title={t('enum.registrationStatus.NOT_REGISTERED')} >
                            <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                                <NotRegisteredIcon />
                            </Box>
                        </Tooltip>
                    )
                } else if ('MISSING_FULL' == registrationStatus) {
                    return (
                        <Tooltip title={t('enum.registrationStatus.MISSING_FULL')}>
                            <Box style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: '100%' }}>
                                <MissingFullIcon />
                            </Box>
                        </Tooltip>
                    )
                }
                return <div className="text-right font-medium">{registrationStatus}</div>
            },
            minWidth: 50,

        }
    )
    columns.push(
        {
            field: 'changeType',
            headerName: t('property.registrationRecord.changeType'),
            description: t('property.registrationRecord.changeTypeDescription'),
            type: 'singleSelect',
            valueOptions: basicValueOptions.changeTypeOptions,
            renderCell: (params) => {
                const changeType = params.row.changeType;

                if ('NEW' == changeType) {
                    return (
                        <Tooltip title={t('enum.changeType.NEW')}>
                            <Add width={20} />
                        </Tooltip>
                    )
                } else if ('EXISTING' == changeType) {
                    return (
                        <Tooltip title={t('enum.changeType.EXISTING')}>
                            <Equal width={20} />
                        </Tooltip>
                    )
                } else if ('DELETED' == changeType) {
                    return (
                        <Tooltip title={t('enum.changeType.DELETED')}>
                            <Remove width={20} />
                        </Tooltip>
                    )
                }
                return <div className="text-right font-medium">{changeType}</div>
            },
            minWidth: 50,

        }
    )
    if (large || xlarge) {
        columns.push(
            {
                field: 'compareType',
                headerName: t('property.registrationRecord.compareType'),
                description: t('property.registrationRecord.compareTypeDescription'),
                type: 'singleSelect',
                valueOptions: basicValueOptions.changeTypeOptions,
                renderCell: (params) => {
                    const compareType = params.row.compareType;

                    if ('NEW' == compareType) {
                        return (
                            <Tooltip title={t('enum.changeType.NEW')}>
                                <Add width={20} />
                            </Tooltip>
                        )
                    } else if ('EXISTING' == compareType) {
                        return (
                            <Tooltip title={t('enum.changeType.EXISTING')}>
                                <Equal width={20} />
                            </Tooltip>
                        )
                    } else if ('DELETED' == compareType) {
                        return (
                            <Tooltip title={t('enum.changeType.DELETED')}>
                                <Remove width={20} />
                            </Tooltip>
                        )
                    }
                    return <div className="text-right font-medium">{compareType}</div>
                },
                minWidth: 50,

            }
        )
    }

    columns.push(
        {
            field: 'paymentTroop',
            headerName: t('property.registrationRecord.paymentTroop'),
            valueGetter: (_value, row) => t('enum.payment.' + row.paymentTroop),
            minWidth: 50,

        }
    )
    columns.push(
        {
            field: 'paymentDivision',
            headerName: t('property.registrationRecord.paymentDivision'),
            valueGetter: (_value, row) => t('enum.payment.' + row.paymentDivision),
            minWidth: 50,

        }
    )
    columns.push(
        {
            field: 'paymentFederation',
            headerName: t('property.registrationRecord.paymentFederation'),
            valueGetter: (_value, row) => t('enum.payment.' + row.paymentFederation),
            minWidth: 50,

        }
    )

    columns.push(
        {
            field: 'relationlist',
            headerName: t('property.registrationRecord.relationlist'),
            minWidth: 100,

        }
    )

    columns.push(
        {
            field: 'snapshot.createDate',
            headerName: t('property.registrationRecord.createDate'),
            type: 'date',
            valueGetter: (_value, row) => row.snapshot.createDate && new Date(row.snapshot.createDate),
            minWidth: 120,

        }
    )

    const initialVisibilityModel = {
        'member': xsmall || small || medium,
        'firstname': large || xlarge,
        'lastname': large || xlarge,
        'scoutId': large || xlarge,
        'sectionMedusaAbbreviation': large || xlarge,
        'jobs': large || xlarge,
        'troopName': large || xlarge,
        'divisionName': large || xlarge,
        'relationlist': large || xlarge,
        'scoutstate': true,
        'registrationStatus': true,
        'paymentTroop': false,
        'paymentDivision': false,
        'paymentFederation': false,
        'snapshot.createDate': false,
        'changeType': large || xlarge,
        'compareType': false,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('registration', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const initialFilerModel = {
        items: [
            {
                field: 'scoutstate',
                operator: 'isAnyOf' as GridLogicOperator,
                value: ['MEMBER_FULL', 'MEMBER_ADD'],
            },
        ],
    }

    const exportBlob = (blob: Blob, filename: string) => {
        // Save the blob in a json file
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();

        setTimeout(() => {
            URL.revokeObjectURL(url);
        });
    };

    interface ExcelExportMenuItemProps extends GridExportMenuItemProps<object> {
        scope: string;
        label?: string;
        troopUuid?: string;
        divisionUuid?: string;
    }

    function ExcelExportMenuItem(props: ExcelExportMenuItemProps) {
        const { hideMenu, scope } = props;

        return (

            <MenuItem
                onClick={() => {

                    fetchRegistrationReport(scope, selectedSnapshotUuid).then((response) => {
                        if (response) {
                            enqueueSnackbar(t('action.downloadCompleted'), { variant: 'success' });
                            const base64 = response.data.base64 || ''
                            // console.log(base64)
                            const raw = Buffer.from(base64, 'base64')
                            // console.log(raw)
                            const blob = new Blob([raw], {
                                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            });
                            exportBlob(blob, response.data.filename || 'Registrierung.xlsx')
                        }
                    })

                    // Hide the export menu after the export
                    hideMenu?.();
                    enqueueSnackbar(t('action.downloadRequested'));
                }}
            >
                {props.label || t('button.registrationExport.' + scope)}
            </MenuItem>
        );
    }

    function PdfExportMenuItem(props: ExcelExportMenuItemProps) {
        const { hideMenu, scope } = props;

        return (

            <MenuItem
                onClick={() => {

                    fetchRegistrationReport(scope, selectedSnapshotUuid).then((response) => {
                        if (response) {
                            enqueueSnackbar(t('action.downloadCompleted'), { variant: 'success' });
                            const base64 = response.data.base64 || ''
                            // console.log(base64)
                            const raw = Buffer.from(base64, 'base64')
                            // console.log(raw)
                            const blob = new Blob([raw], {
                                type: 'application/pdf',
                            });
                            exportBlob(blob, response.data.filename || 'Registrierung.pdf')
                        }
                    })

                    // Hide the export menu after the export
                    hideMenu?.();
                    enqueueSnackbar(t('action.downloadRequested'));
                }}
            >
                {props.label || t('button.registrationExport.' + scope)}
            </MenuItem>
        );
    }

    function ZipExportMenuItem(props: ExcelExportMenuItemProps) {
        const { hideMenu, scope } = props;

        return (

            <MenuItem
                onClick={() => {

                    fetchRegistrationReport(scope, selectedSnapshotUuid).then((response) => {
                        if (response) {
                            enqueueSnackbar(t('action.downloadCompleted'), { variant: 'success' });
                            const base64 = response.data.base64 || ''
                            // console.log(base64)
                            const raw = Buffer.from(base64, 'base64')
                            // console.log(raw)
                            const blob = new Blob([raw], {
                                type: 'application/zip',
                            });
                            exportBlob(blob, response.data.filename || 'Registrierung.zip')
                        }
                    })

                    // Hide the export menu after the export
                    hideMenu?.();
                    enqueueSnackbar(t('action.downloadRequested'));
                }}
            >
                <ListItemIcon>
                    <FolderZipIcon />
                </ListItemIcon>
                <ListItemText>
                    {props.label || t('button.registrationExport.' + scope)}
                </ListItemText>
            </MenuItem>
        );
    }

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogScope, setDialogScope] = React.useState('');

    const handleClose = () => {
        setDialogOpen(false);
    };

    const openFinanceTroopsDialog = () => {
        setDialogScope('registration_finance_division');
        setDialogOpen(true);
    }

    const openFinanceDivisionsDialog = () => {
        setDialogScope('registration_finance_federation');
        setDialogOpen(true);
    }

    const startReport = (scope: string, orgUuid: string) => () => {
        fetchRegistrationReportOrg(scope, selectedSnapshotUuid, orgUuid).then((response) => {
            if (response) {
                enqueueSnackbar(t('action.downloadCompleted'), { variant: 'success' });
                const base64 = response.data.base64 || ''
                // console.log(base64)
                const raw = Buffer.from(base64, 'base64')
                // console.log(raw)
                const blob = new Blob([raw], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                exportBlob(blob, response.data.filename || 'Registrierung.xlsx')
            }
        })

        // Hide the export menu after the export
        handleClose()
        enqueueSnackbar(t('action.downloadRequested'))
    }

    function ReportExportButton(props: ButtonProps) {
        return (
            <GridToolbarReportContainer {...props}>
                {
                    sessionOrganization.organizationSystem === 'PFADFINDER' && sessionOrganization.organizationLevel == OrganizationLevelType.TROOP &&
                    <>
                        <ExcelExportMenuItem scope='registration_finance_troop' label={t('button.registrationExport.registration_finance_troop')} />
                        <ExcelExportMenuItem scope='registration_finance_division' label={t('button.registrationExport.registration_finance_division_TROOP')} />
                        <Divider />
                        <ExcelExportMenuItem scope='registration_job_section_division' />
                        {/* <ExcelExportMenuItem scope='registration_statistic_federation_troops' /> */}
                        <ExcelExportMenuItem scope='registration_history' />
                    </>
                }
                {
                    sessionOrganization.organizationSystem === 'PFADFINDER' && sessionOrganization.organizationLevel == OrganizationLevelType.DIVISION &&
                    <>
                        <ExcelExportMenuItem scope='registration_finance_division' label={t('button.registrationExport.registration_finance_division_DIVISION')} />
                        <ExcelExportMenuItem scope='registration_finance_federation' label={t('button.registrationExport.registration_finance_federation_DIVISION')} />
                        <Divider />
                        <ExcelExportMenuItem scope='registration_job_section_division' />
                        <ExcelExportMenuItem scope='registration_statistic_federation_troops' />
                        <ExcelExportMenuItem scope='registration_statistic_federation_divisions' />
                        <ExcelExportMenuItem scope='registration_statistic_federation_divisions_history' />
                        <ExcelExportMenuItem scope='registration_statistic_federation_divisions_ideell' />
                        <ExcelExportMenuItem scope='registration_history' />
                        <ExcelExportMenuItem scope='registration_history_troops' />
                        <Divider />
                        <ZipExportMenuItem scope='registration_finance_division_zip' label={t('button.registrationExport.registration_finance_troops')} />
                        <MenuItem onClick={openFinanceTroopsDialog}>
                            <ListItemIcon>
                                <KeyboardArrowRightIcon />
                            </ListItemIcon>
                            <ListItemText>
                                {t('button.registrationExport.registration_finance_troops')}
                            </ListItemText>
                        </MenuItem>
                    </>
                }
                {
                    sessionOrganization.organizationSystem === 'PFADFINDER' && sessionOrganization.organizationLevel == OrganizationLevelType.FEDERATION &&
                    <>

                        <ExcelExportMenuItem scope='registration_finance_federation' label={t('button.registrationExport.registration_finance_federation_FEDERATION')} />
                        <Divider />
                        <ExcelExportMenuItem scope='registration_job_section_federation' />

                        <ExcelExportMenuItem scope='registration_statistic_federation_troops' />
                        <ExcelExportMenuItem scope='registration_statistic_federation_divisions' />
                        <ExcelExportMenuItem scope='registration_statistic_federation' />

                        <ExcelExportMenuItem scope='registration_statistic_federation_divisions_history' />
                        <ExcelExportMenuItem scope='registration_statistic_federation_divisions_ideell' />
                        <ExcelExportMenuItem scope='registration_history' />

                        <Divider />
                        <ZipExportMenuItem scope='registration_finance_federation_zip' label={t('button.registrationExport.registration_finance_divisions')} />
                        <MenuItem onClick={openFinanceDivisionsDialog}>
                            <ListItemIcon>
                                <KeyboardArrowRightIcon />
                            </ListItemIcon>
                            <ListItemText>
                                {t('button.registrationExport.registration_finance_divisions')}
                            </ListItemText>
                        </MenuItem>
                        {scoregAbrechnung && (
                            <>
                                <Divider />
                                <PdfExportMenuItem scope='scoreg_abrechnung' />
                            </>
                        )}
                    </>
                }
                {
                    sessionOrganization.organizationSystem === 'GILDE' && sessionOrganization.organizationLevel == OrganizationLevelType.FEDERATION &&
                    <>

                        <ExcelExportMenuItem scope='registration_finance_gildeverband' label={t('button.registrationExport.registration_finance_gildeverband')} />
                    </>
                }
            </GridToolbarReportContainer >
        );
    }

    function CreateManualSnapshotButton() {
        return (
            <Tooltip title={t('button.registrationSnapshot.createManual')}>
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                        createManualSnapshotMutation.mutate()
                        enqueueSnackbar(t('action.manualSnapshotCreationTriggered'));
                    }}
                    startIcon={<Add width={20} />}>
                    {t('button.registrationSnapshot.createManual')}
                </Button>
            </Tooltip>
        )
    }

    const generateFileName = () => {
        return t('report.name.registration') + "_" + sessionOrganization.abbreviation + "_" + new Date().toISOString().slice(0, 19).replace(/:/g, "").replace(/-/g, "").replace(/T/g, "");
    }

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                {responsive.minMedium &&
                    <>
                        <GridToolbarFilterButton />
                        <ReportExportButton />
                        {createSnapshot &&
                            <CreateManualSnapshotButton />
                        }
                        <GridToolbarExport
                            csvOptions={{ disableToolbarButton: true }}
                            printOptions={{ disableToolbarButton: true }}
                            excelOptions={{ fileName: generateFileName() }} />

                        <ScoregQuickFilter />
                    </>
                }
                {responsive.minMedium &&
                    <GridPagination />
                }
                {(responsive.xsmall || responsive.small) &&
                    <GridToolbarPaginationButton />
                }

            </GridToolbarContainer>
        )
    }

    const responsive = useResponsive();

    const defaultLocaleText = deDE.components.MuiDataGrid.defaultProps.localeText
    const localeText = { ...defaultLocaleText, ...{ toolbarExportLabel: 'Export' } }

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('registration', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('registration', 35));

    if (registrationQuery.isLoading || registrationQuery.isFetching || !registrations) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <Grid container>
                <Grid size={{ lg: 3, sm: 6, xs: 12 }} sx={{ padding: 1 }}>
                    <FormControl fullWidth variant='standard' sx={{ minWidth: 120 }}>
                        <InputLabel>{t('label.registration.snapshot')}</InputLabel>
                        <Select
                            value={selectedSnapshotUuid}
                            onChange={handleSnapshotChange}>
                            {registrationSnapshots && registrationSnapshots.map((snapshot) => {
                                return <MenuItem key={snapshot.uuid} value={snapshot.uuid}>{t('enum.registrationSnapshotType.' + snapshot.type)} {dayjs(snapshot.createDate).format('DD.MM.YYYY')}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid
                    size={{ lg: 3, sm: 6, xs: 12 }}
                    sx={{
                        display: { lg: 'block', md: 'none', sm: 'none', xs: 'none' },
                        padding: 1
                    }}>
                    <FormControl fullWidth variant='standard' sx={{ minWidth: 120 }}>
                        <InputLabel>{t('label.registration.compare')}</InputLabel>
                        <Select
                            value={selectedCompareUuid}
                            onChange={handleCompareChange}>
                            <MenuItem key='' value='' selected>{t('label.registration.noComparison')}</MenuItem>
                            {registrationSnapshots && registrationSnapshots.map((snapshot) => {
                                return <MenuItem key={snapshot.uuid} value={snapshot.uuid}>{t('enum.registrationSnapshotType.' + snapshot.type)} {dayjs(snapshot.createDate).format('DD.MM.YYYY')}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ lg: 6, sm: 6, xs: 12 }} sx={{ padding: 1 }} >
                    {snapshotVersion == 1 &&
                        <TextField
                            value={'Gesamt: ' + total + ' | Vollmitglieder: ' + memberFull + ' | Zusatzmitglieder: ' + memberAdd}
                            variant='standard'
                            label={t('label.registration.statistic')}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    disableUnderline: true,
                                },
                            }} />
                    }
                    {snapshotVersion == 2 &&
                        <TextField
                            value={'Gesamt: ' + total}
                            variant='standard'
                            label={t('label.registration.statistic')}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    disableUnderline: true,
                                },
                            }} />
                    }
                    {selectedCompareUuid && compareVersion == 1 &&
                        <TextField
                            value={'Gesamt: ' + totalComp}
                            variant='standard'
                            label={t('label.registration.statisticCompare')}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    disableUnderline: true,
                                },
                            }} />
                    }
                    {selectedCompareUuid && compareVersion == 2 &&
                        <TextField
                            value={'Gesamt: ' + totalComp}
                            variant='standard'
                            label={t('label.registration.statisticCompare')}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    disableUnderline: true,
                                },
                            }} />
                    }
                    {selectedCompareUuid && compareVersion == 1 && snapshotVersion == 1 &&
                        <TextField
                            value={'Gesamt: ' + totalDiff + ' | Vollmitglieder: ' + memberFullDiff + ' | Zusatzmitglieder: ' + memberAddDiff}
                            variant='standard'
                            label={t('label.registration.difference')}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    disableUnderline: true,
                                },
                            }} />
                    }
                    {selectedCompareUuid && compareVersion == 2 && snapshotVersion == 2 &&
                        <TextField
                            value={'Gesamt: ' + totalDiff}
                            variant='standard'
                            label={t('label.registration.difference')}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    disableUnderline: true,
                                },
                            }} />
                    }
                </Grid>

            </Grid>
            <Dialog open={dialogOpen} onClose={handleClose}>
                <DialogTitle>{t('button.registrationExport.' + dialogScope)}</DialogTitle>
                <DialogContent>
                    <Select sx={{ width: '100%' }}>
                        {organizations && organizations.map((org) => {
                            return <MenuItem key={org.uuid} value={org.uuid} onClick={startReport(dialogScope, org.uuid)}>{org.name}</MenuItem>
                        })}
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{t('action.cancel')}</Button>
                </DialogActions>
            </Dialog>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <DataGridPremium
                    columns={columns}
                    rows={registrations}
                    getRowId={(row) => { return row.uuid || '' }}
                    localeText={localeText}
                    pagination
                    pageSizeOptions={pageSizeOptions}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model)
                        gridPageSize.handlePaginationModelChange('awards', model)
                    }}
                    slots={{ toolbar: CustomToolbar }}
                    slotProps={{
                        filterPanel: {
                            logicOperators: [GridLogicOperator.And],
                            sx: { maxWidth: '100vw' }
                        },
                    }}
                    density='compact'
                    columnVisibilityModel={visibilityModel}
                    onColumnVisibilityModelChange={handleVisibilityModelChange}
                    initialState={
                        {
                            filter: {
                                filterModel: initialFilerModel
                            }
                        }
                    }
                    hideFooter={true}
                    disableDensitySelector
                    disableAggregation
                    disableColumnPinning
                    disableColumnReorder
                    disableRowGrouping
                    disablePivoting
                    showToolbar />
            </div>
        </Box>
    );
}
